/**
 * loadDetailsWorkbookBuilder.ts
 * Builds a real .xlsx workbook for the ASN Gap Load Details table using
 * ExcelJS — a flat (non-grouped) sheet: one header row (the table's own 23
 * column labels, bold, light fill), then one row per exported
 * `ASNLoadDetailRow`, in that same column order. The top row is frozen so
 * headers stay visible while scrolling a large export.
 */

import ExcelJS from 'exceljs';
import { ASNLoadDetailRow } from '../api/asnOverviewKpiApi';
import { LOAD_DETAILS_COLUMNS, LoadDetailsColumnDef } from '../constants/loadDetailsColumns';
import { mapApiRowToTableRow } from './loadDetailsExportMapper';

const HEADER_FILL_HEX = 'F8FAFD';
const HEADER_FONT_COLOR_HEX = '1A1D21';
const DIMENSION_COL_WIDTH = 22;
const NUMERIC_COL_WIDTH = 12;

/**
 * Builds the Load Details export workbook. `rows` should already be the
 * full, combined row set across every page (see `fetchAsnLoadDetailsAllPages`)
 * — this function performs no pagination/fetching of its own.
 */
export const buildLoadDetailsWorkbook = (
  rows: ASNLoadDetailRow[],
  columns: LoadDetailsColumnDef[] = LOAD_DETAILS_COLUMNS,
): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Load Details', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  ws.addRow(columns.map((col) => col.label));
  rows.forEach((row) => ws.addRow(mapApiRowToTableRow(row, columns)));

  ws.columns = columns.map((col) => ({ width: col.numeric ? NUMERIC_COL_WIDTH : DIMENSION_COL_WIDTH }));

  const headerRow = ws.getRow(1);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, size: 12, color: { argb: `FF${HEADER_FONT_COLOR_HEX}` } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_FILL_HEX}` } };
  });

  return workbook;
};

export default buildLoadDetailsWorkbook;
